import { useCallback, useState } from 'react';
import { LinkedinTypes } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import {
  EmailAccountTypes,
  EmailCredentialErrors,
  EmailCredentials,
  Encryptions,
  LinkedinAccount,
  OAuthCredentials,
} from '../linkedin-accounts.types';

/**
 * Consolidated hook for all dialog states in linkedin-account-overview.tsx
 * This replaces ~180 lines of useState declarations with a single hook call
 */

// ============ State Interfaces ============

interface RemoveLinkedinAccountDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  loading: boolean;
}

interface EditLinkedinAccountGeneralSettingsDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  selectedCountry: OptionType;
  selectedCity: OptionType;
  linkedinProduct: LinkedinTypes;
  loading: boolean;
}

interface EditLinkedinAccountLoginInformationDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  userName: string;
  password: string;
  loading: boolean;
}

interface EditEmailCredentialsDialogState {
  open: boolean;
  type: EmailAccountTypes;
  smtp: EmailCredentials;
  imap: EmailCredentials;
  oauth: OAuthCredentials;
  account: LinkedinAccount | null;
  error: EmailCredentialErrors | undefined;
  accessRevoked: boolean;
  loading: boolean;
}

interface EditLinkedinAccountNameDialogState {
  open: boolean;
  loading: boolean;
  account: LinkedinAccount | null;
  firstName: string;
  lastName: string;
}

interface RecruiterParallelLoginDialogState {
  open: boolean;
  loading: boolean;
  account: LinkedinAccount | null;
}

interface OnboardingIncompleteDialogState {
  open: boolean;
  loading: boolean;
  account: LinkedinAccount | null;
}

interface FairUsePauseDialogState {
  open: boolean;
}

interface TwoFaNotEnabledDialogState {
  open: boolean;
  account: LinkedinAccount | null;
}

interface ManageEmailSendersDialogState {
  open: boolean;
  account: LinkedinAccount | null;
}

interface EditLinkedinAccountLimitsDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  workDays: string[];
  workHours: number[];
  /** Hours the account had stored before we trimmed it to the current rules, else null. */
  workHoursTrimmedFrom: number | null;
  timeZone: OptionType;
  connectionsLimit: number | null;
  messagesLimit: number | null;
  emailsLimit: number | null;
  coldCallsLimit: number | null;
  cooldownAfterMessages: number | null;
  cooldownAfterVisits: number | null;
  visitDailyLimit: number | null;
  inmailDailyLimit: number | null;
  loading: boolean;
}

interface ResetLocalStorageDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  loading: boolean;
}

interface QuotaSplitDialogState {
  open: boolean;
  loading: boolean;
  funnels: any[];
  editedWeights: Record<string, any>;
  accountLimits: { connections: number; messages: number; emails: number; coldCalls: number; visits: number; inMails: number };
}

interface EditSignatureDialogState {
  open: boolean;
  account: LinkedinAccount | null;
  signature: string;
  loading: boolean;
}

// ============ Initial States ============

const defaultEmailCredentials: EmailCredentials = {
  server: '',
  port: '',
  encryption: Encryptions.NONE,
  username: '',
  password: '',
};

const defaultOAuthCredentials: OAuthCredentials = {
  email: '',
  code: '',
};

const initialRemoveLinkedinAccountDialog: RemoveLinkedinAccountDialogState = {
  open: false,
  account: null,
  loading: false,
};

const initialEditLinkedinAccountGeneralSettingsDialog: EditLinkedinAccountGeneralSettingsDialogState = {
  open: false,
  account: null,
  selectedCountry: { label: '', value: '' },
  selectedCity: { label: '', value: '' },
  linkedinProduct: LinkedinTypes.LINKEDIN,
  loading: false,
};

const initialEditLinkedinAccountLoginInformationDialog: EditLinkedinAccountLoginInformationDialogState = {
  open: false,
  account: null,
  userName: '',
  password: '',
  loading: false,
};

const initialEditEmailCredentialsDialog: EditEmailCredentialsDialogState = {
  open: false,
  type: EmailAccountTypes.OTHER,
  smtp: defaultEmailCredentials,
  imap: defaultEmailCredentials,
  oauth: defaultOAuthCredentials,
  account: null,
  error: undefined,
  accessRevoked: false,
  loading: false,
};

const initialEditLinkedinAccountNameDialog: EditLinkedinAccountNameDialogState = {
  open: false,
  loading: false,
  account: null,
  firstName: '',
  lastName: '',
};

const initialRecruiterParallelLoginDialog: RecruiterParallelLoginDialogState = {
  open: false,
  loading: false,
  account: null,
};

const initialOnboardingIncompleteDialog: OnboardingIncompleteDialogState = {
  open: false,
  loading: false,
  account: null,
};

const initialFairUsePauseDialog: FairUsePauseDialogState = {
  open: false,
};

const initialTwoFaNotEnabledDialog: TwoFaNotEnabledDialogState = {
  open: false,
  account: null,
};

const initialManageEmailSendersDialog: ManageEmailSendersDialogState = {
  open: false,
  account: null,
};

const initialEditLinkedinAccountLimitsDialog: EditLinkedinAccountLimitsDialogState = {
  open: false,
  account: null,
  workDays: [],
  workHours: [],
  workHoursTrimmedFrom: null,
  timeZone: { label: '', value: '' },
  connectionsLimit: null,
  messagesLimit: null,
  emailsLimit: null,
  coldCallsLimit: null,
  cooldownAfterMessages: null,
  cooldownAfterVisits: null,
  visitDailyLimit: null,
  inmailDailyLimit: null,
  loading: false,
};

const initialResetLocalStorageDialog: ResetLocalStorageDialogState = {
  open: false,
  account: null,
  loading: false,
};

interface AccountConnectionsDialogState {
  open: boolean;
  account: LinkedinAccount | null;
}

const initialAccountConnectionsDialog: AccountConnectionsDialogState = {
  open: false,
  account: null,
};

const initialQuotaSplitDialog: QuotaSplitDialogState = {
  open: false,
  loading: false,
  funnels: [],
  editedWeights: {},
  accountLimits: { connections: 0, messages: 0, emails: 0, coldCalls: 0, visits: 0, inMails: 0 },
};

const initialEditSignatureDialog: EditSignatureDialogState = {
  open: false,
  account: null,
  signature: '',
  loading: false,
};

// ============ Hook ============

export function useLinkedinOverviewDialogs() {
  // All dialog states
  const [removeLinkedinAccountDialog, setRemoveLinkedinAccountDialog] = useState(initialRemoveLinkedinAccountDialog);
  const [editLinkedinAccountGeneralSettingsDialog, setEditLinkedinAccountGeneralSettingsDialog] =
    useState(initialEditLinkedinAccountGeneralSettingsDialog);
  const [editLinkedinAccountLoginInformationDialog, setEditLinkedinAccountLoginInformationDialog] =
    useState(initialEditLinkedinAccountLoginInformationDialog);
  const [editEmailCredentialsDialog, setEditEmailCredentialsDialog] = useState(initialEditEmailCredentialsDialog);
  const [editLinkedinAccountNameDialog, setEditLinkedinAccountNameDialog] = useState(initialEditLinkedinAccountNameDialog);
  const [recruiterParallelLoginDialog, setRecruiterParallelLoginDialog] = useState(initialRecruiterParallelLoginDialog);
  const [onboardingIncompleteDialog, setOnboardingIncompleteDialog] = useState(initialOnboardingIncompleteDialog);
  const [fairUsePauseDialog, setFairUsePauseDialog] = useState(initialFairUsePauseDialog);
  const [twoFaNotEnabledDialog, setTwoFaNotEnabledDialog] = useState(initialTwoFaNotEnabledDialog);
  const [manageEmailSendersDialog, setManageEmailSendersDialog] = useState(initialManageEmailSendersDialog);
  const [editLinkedinAccountLimitsDialog, setEditLinkedinAccountLimitsDialog] = useState(initialEditLinkedinAccountLimitsDialog);
  const [resetLocalStorageDialog, setResetLocalStorageDialog] = useState(initialResetLocalStorageDialog);
  const [accountConnectionsDialog, setAccountConnectionsDialog] = useState(initialAccountConnectionsDialog);
  const [quotaSplitDialog, setQuotaSplitDialog] = useState(initialQuotaSplitDialog);
  const [editSignatureDialog, setEditSignatureDialog] = useState(initialEditSignatureDialog);

  // Reset functions
  const resetRemoveLinkedinAccountDialog = useCallback(() => setRemoveLinkedinAccountDialog(initialRemoveLinkedinAccountDialog), []);
  const resetEditLinkedinAccountGeneralSettingsDialog = useCallback(
    () => setEditLinkedinAccountGeneralSettingsDialog(initialEditLinkedinAccountGeneralSettingsDialog),
    [],
  );
  const resetEditLinkedinAccountLoginInformationDialog = useCallback(
    () => setEditLinkedinAccountLoginInformationDialog(initialEditLinkedinAccountLoginInformationDialog),
    [],
  );
  const resetEditEmailCredentialsDialog = useCallback(() => setEditEmailCredentialsDialog(initialEditEmailCredentialsDialog), []);
  const resetEditLinkedinAccountNameDialog = useCallback(() => setEditLinkedinAccountNameDialog(initialEditLinkedinAccountNameDialog), []);
  const resetRecruiterParallelLoginDialog = useCallback(() => setRecruiterParallelLoginDialog(initialRecruiterParallelLoginDialog), []);
  const resetOnboardingIncompleteDialog = useCallback(() => setOnboardingIncompleteDialog(initialOnboardingIncompleteDialog), []);
  const resetFairUsePauseDialog = useCallback(() => setFairUsePauseDialog(initialFairUsePauseDialog), []);
  const resetTwoFaNotEnabledDialog = useCallback(() => setTwoFaNotEnabledDialog(initialTwoFaNotEnabledDialog), []);
  const resetManageEmailSendersDialog = useCallback(() => setManageEmailSendersDialog(initialManageEmailSendersDialog), []);
  const resetEditLinkedinAccountLimitsDialog = useCallback(() => setEditLinkedinAccountLimitsDialog(initialEditLinkedinAccountLimitsDialog), []);
  const resetResetLocalStorageDialog = useCallback(() => setResetLocalStorageDialog(initialResetLocalStorageDialog), []);
  const resetAccountConnectionsDialog = useCallback(() => setAccountConnectionsDialog(initialAccountConnectionsDialog), []);
  const resetQuotaSplitDialog = useCallback(() => setQuotaSplitDialog(initialQuotaSplitDialog), []);
  const resetEditSignatureDialog = useCallback(() => setEditSignatureDialog(initialEditSignatureDialog), []);

  return {
    // States and setters (for direct state manipulation in handlers)
    removeLinkedinAccountDialog,
    setRemoveLinkedinAccountDialog,
    resetRemoveLinkedinAccountDialog,

    editLinkedinAccountGeneralSettingsDialog,
    setEditLinkedinAccountGeneralSettingsDialog,
    resetEditLinkedinAccountGeneralSettingsDialog,

    editLinkedinAccountLoginInformationDialog,
    setEditLinkedinAccountLoginInformationDialog,
    resetEditLinkedinAccountLoginInformationDialog,

    editEmailCredentialsDialog,
    setEditEmailCredentialsDialog,
    resetEditEmailCredentialsDialog,

    editLinkedinAccountNameDialog,
    setEditLinkedinAccountNameDialog,
    resetEditLinkedinAccountNameDialog,

    recruiterParallelLoginDialog,
    setRecruiterParallelLoginDialog,
    resetRecruiterParallelLoginDialog,

    onboardingIncompleteDialog,
    setOnboardingIncompleteDialog,
    resetOnboardingIncompleteDialog,

    fairUsePauseDialog,
    setFairUsePauseDialog,
    resetFairUsePauseDialog,

    twoFaNotEnabledDialog,
    setTwoFaNotEnabledDialog,
    resetTwoFaNotEnabledDialog,

    manageEmailSendersDialog,
    setManageEmailSendersDialog,
    resetManageEmailSendersDialog,

    editLinkedinAccountLimitsDialog,
    setEditLinkedinAccountLimitsDialog,
    resetEditLinkedinAccountLimitsDialog,

    resetLocalStorageDialog,
    setResetLocalStorageDialog,
    resetResetLocalStorageDialog,

    accountConnectionsDialog,
    setAccountConnectionsDialog,
    resetAccountConnectionsDialog,

    quotaSplitDialog,
    setQuotaSplitDialog,
    resetQuotaSplitDialog,

    editSignatureDialog,
    setEditSignatureDialog,
    resetEditSignatureDialog,

    // Constants for resetting
    initialEditEmailCredentialsDialog,
    defaultEmailCredentials,
    defaultOAuthCredentials,
  };
}

// Export types for use in component
export type {
  RemoveLinkedinAccountDialogState,
  EditLinkedinAccountGeneralSettingsDialogState,
  EditLinkedinAccountLoginInformationDialogState,
  EditEmailCredentialsDialogState,
  EditLinkedinAccountNameDialogState,
  RecruiterParallelLoginDialogState,
  OnboardingIncompleteDialogState,
  FairUsePauseDialogState,
  TwoFaNotEnabledDialogState,
  ManageEmailSendersDialogState,
  EditLinkedinAccountLimitsDialogState,
  ResetLocalStorageDialogState,
  QuotaSplitDialogState,
  EditSignatureDialogState,
};

